import { SELECTORS } from "../Constants";
import { cancelBooking, fetchBookings } from "../API";
import { bookingRecord } from "../API/types";
import LoaderOverlay from "../../LoaderOverlay";
import { Toast } from "../../../Components/Dialog/Toast";
import { DateTime } from "@easepick/bundle";
import { Modal } from "../../../Components/Dialog/Modal";

const DATE_FORMAT_API_RESPONSE = "YYYY-MM-DD";
const DATETIME_FORMAT_API_RESPONSE = "YYYY-MM-DD hh:mm:ss";

const CLASSES = {
    INACTIVE: "inactive"
};

export class List {
    private readonly tab: HTMLSpanElement;
    private readonly loaderOverlay: LoaderOverlay;
    private list: bookingRecord[] | null = null;
    private noItemsTemplate: HTMLTemplateElement;
    private itemsTemplate: HTMLTemplateElement;
    private upcomingContainer: HTMLDivElement;
    private expiredContainer: HTMLDivElement;
    private cancelledContainer: HTMLDivElement;

    constructor(private readonly container: HTMLDivElement) {
        this.container = container;
        this.upcomingContainer = this.container.querySelector<HTMLDivElement>(SELECTORS.upcomingList);
        this.expiredContainer = this.container.querySelector<HTMLDivElement>(SELECTORS.expiredList);
        this.cancelledContainer = this.container.querySelector<HTMLDivElement>(SELECTORS.cancelledList);
        this.tab = document.querySelector(SELECTORS.accountTab);
        this.loaderOverlay = new LoaderOverlay();
        this.noItemsTemplate = document.querySelector<HTMLTemplateElement>(SELECTORS.accountNoItemsTemplate);
        this.itemsTemplate = document.querySelector<HTMLTemplateElement>(SELECTORS.accountItemsTemplate);
    }

    public init() {
        this.tab.addEventListener("tab-active", this.show.bind(this));
    }

    private async show() {
        await this.getBookings();
        this.clearContainer();
        if (this.list.length === 0) {
            this.renderNoItems();
        } else {
            this.renderItems();
        }
    }

    private renderItems() {
        this.list.forEach((i) => {
            const content = this.itemsTemplate.content.cloneNode(true) as HTMLDivElement;
            const startDate = new DateTime(i.start_date, DATETIME_FORMAT_API_RESPONSE);
            const item = content.querySelector<HTMLDivElement>(SELECTORS.accountItem.item);
            const expired = startDate.toJSDate().valueOf() < Date.now();

            const localeStartDate = startDate.toJSDate().toLocaleDateString(undefined, {
                dateStyle: "medium"
            });

            const localeStartTime = startDate.toJSDate().toLocaleTimeString(undefined, {
                timeStyle: "short"
            });

            item.querySelector<HTMLSpanElement>(
                SELECTORS.accountItem.dateTime
            ).innerText = `${localeStartDate} ${localeStartTime}`;
            item.querySelector<HTMLSpanElement>(SELECTORS.accountItem.event).innerText = i.event;

            if (i.location_details) {
                const locationContainer = item.querySelector<HTMLSpanElement>(SELECTORS.accountItem.location.container);
                locationContainer.querySelector<HTMLSpanElement>(SELECTORS.accountItem.location.name).innerText =
                    i.location_details.title;

                locationContainer.querySelector<HTMLSpanElement>(
                    SELECTORS.accountItem.location.addressItems.address1
                ).innerText = i.location_details.address1;
                locationContainer.querySelector<HTMLSpanElement>(
                    SELECTORS.accountItem.location.addressItems.address2
                ).innerText = i.location_details.address2;
                locationContainer.querySelector<HTMLSpanElement>(
                    SELECTORS.accountItem.location.addressItems.zip
                ).innerText = i.location_details.zip;
                locationContainer.querySelector<HTMLSpanElement>(
                    SELECTORS.accountItem.location.addressItems.city
                ).innerText = i.location_details.city;
                locationContainer.querySelector<HTMLAnchorElement>(SELECTORS.accountItem.location.mapsLink).href =
                    i.location_details.mapped_fields.maps_link;
            }

            item.querySelector<HTMLSpanElement>(SELECTORS.accountItem.booking.code).innerHTML = i.code || "-";
            item.querySelector<HTMLSpanElement>(SELECTORS.accountItem.booking.status).innerHTML = i.status?.name || "-";

            if (i.additional_fields) {
                const additional = item.querySelector<HTMLSpanElement>(SELECTORS.accountItem.additional);
                i.additional_fields.forEach((a) => {
                    // cloud (file upload) not supported atm
                    if (a.type === "cloud") return;
                    const label = a.title,
                        value = a.value,
                        item = additional.appendChild(document.createElement("div"));
                    item.dataset.type = a.type;
                    item.appendChild(document.createElement("div"));
                    item.appendChild(document.createElement("div"));
                    (item.childNodes[0] as HTMLDivElement).innerText = label;
                    if (a.type === "checkbox") {
                        (item.childNodes[1] as HTMLDivElement).classList.add("icon", "checkbox", "keep-svg");
                        if (a.value === "1") {
                            (item.childNodes[1] as HTMLDivElement).classList.add("active");
                        }
                    } else if (a.type === "date") {
                        const fieldDate = new DateTime(value, DATE_FORMAT_API_RESPONSE);
                        (item.childNodes[1] as HTMLDivElement).innerHTML = fieldDate
                            .toJSDate()
                            .toLocaleDateString(undefined, {
                                dateStyle: "medium"
                            });
                    } else {
                        (item.childNodes[1] as HTMLDivElement).innerHTML = value;
                    }
                });
            }

            const btnCancel = item.querySelector<HTMLAnchorElement>(SELECTORS.accountItem.btnCancel);
            const statusItem = item.querySelector<HTMLAnchorElement>(SELECTORS.accountItem.booking.status);
            if (expired || i.is_cancelled) {
                btnCancel.remove();
                item.classList.add(CLASSES.INACTIVE);
                if (expired) {
                    statusItem.innerText = window.SuccessMessages.bookingRequest.expired;
                }
                if (i.is_cancelled) {
                    statusItem.innerText = window.SuccessMessages.bookingRequest.cancelled;
                }
            } else {
                btnCancel.dataset.id = i.id;
                btnCancel.addEventListener("click", async () => {
                    try {
                        await new Modal("confirm-cancel-booking")?.confirm();
                        const result = await cancelBooking(btnCancel.dataset.id);
                        if (result) {
                            btnCancel.remove();
                            item.classList.add(CLASSES.INACTIVE);
                            statusItem.innerText = window.SuccessMessages.bookingRequest.cancelled;
                        }
                    } catch (error) {}
                });
            }

            if (expired) {
                this.expiredContainer.appendChild(content);
            } else if (i.is_cancelled) {
                this.cancelledContainer.appendChild(content);
            } else {
                this.upcomingContainer.appendChild(content);
            }
        });
    }

    private renderNoItems() {
        if (!this.container.querySelector(SELECTORS.noItems)) {
            const content = this.noItemsTemplate.content.cloneNode(true);
            this.container.appendChild(content);
        }
    }

    private clearContainer() {
        while (this.upcomingContainer.childNodes.length) {
            this.upcomingContainer.childNodes[0].remove();
        }
        while (this.expiredContainer.childNodes.length) {
            this.expiredContainer.childNodes[0].remove();
        }
        while (this.cancelledContainer.childNodes.length) {
            this.cancelledContainer.childNodes[0].remove();
        }
    }

    private async getBookings() {
        if (!this.list) {
            this.loaderOverlay.render();
            try {
                this.list = await fetchBookings();
            } catch (error) {
                if (error.status === 404) {
                    this.list = [];
                } else {
                    new Toast({
                        title: window.SuccessMessages.bookingRequest.error,
                        type: Toast.TYPE_ERROR
                    })?.render();
                    console.error(error.status);
                }
            } finally {
                this.loaderOverlay.destroy();
            }
        }

        return this.list;
    }
}
